import { z } from "zod";
import { OutboundTransportSchema } from "../schemas/EmailRoutingSchema";

const identifier = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);
const editRevision = z.string().regex(/^edit-[a-f0-9]{64}$/);
const reviewRevision = z.string().regex(/^delivery-[a-f0-9]{64}$/);
export const InvoiceDeliveryChannelSchema = z.enum(["EMAIL", "PEPPOL"]);
export const InvoiceDeliveryBlockCodeSchema = z.enum([
  "READ_ONLY",
  "DOCUMENT_TYPE_UNSUPPORTED",
  "REMINDER_SOURCE_CHANGED",
  "NOT_OUTBOUND",
  "NOT_APPROVED",
  "VALIDATION_REQUIRED",
  "ARTIFACT_UNAVAILABLE",
  "ARTIFACT_CHANGED",
  "PDF_REQUIRED",
  "PDF_FAILED",
  "OUTBOUND_MAIL_DISABLED",
  "PLATFORM_PROVIDER_NOT_ASSIGNED",
  "PLATFORM_PROVIDER_UNAVAILABLE",
  "PLATFORM_PROVIDER_ASSIGNMENT_INVALID",
  "PLATFORM_PROVIDER_NOT_PRODUCTION_ELIGIBLE",
  "SMTP_NOT_CONFIGURED",
  "M365_DELEGATED_NOT_CONNECTED",
  "M365_APPLICATION_NOT_CONFIGURED",
  "M365_CONNECTION_MODE_MISMATCH",
  "M365_PLATFORM_APP_NOT_CONFIGURED",
  "M365_APPLICATION_RBAC_NOT_VERIFIED",
  "M365_APPLICATION_TENANT_SECRET_FORBIDDEN",
  "PEPPOL_NOT_CONFIGURED",
  "PEPPOL_PROVIDER_UNAVAILABLE",
  "PEPPOL_FORMAT_UNSUPPORTED",
  "PEPPOL_RECEIVER_UNAVAILABLE",
  "DELIVERY_IN_PROGRESS",
  "ALREADY_SENT",
]);
export const InvoiceDeliveryStateSchema = z
  .object({
    id: identifier,
    channel: InvoiceDeliveryChannelSchema,
    status: z.enum([
      "QUEUED",
      "SENDING",
      "SENT",
      "DELIVERED",
      "BOUNCED",
      "FAILED",
      "DELIVERY_UNKNOWN",
    ]),
    updatedAt: z.string().datetime(),
    errorCode: z
      .string()
      .regex(/^[A-Z][A-Z0-9_]{0,99}$/)
      .nullable(),
  })
  .strict();
const blocker = z
  .object({
    code: InvoiceDeliveryBlockCodeSchema,
    message: z.string().min(1).max(500),
    settingsTarget: z.enum(["EMAIL", "PEPPOL"]).nullable(),
  })
  .strict();
export const InvoiceDeliveryArtifactSchema = z
  .object({
    id: identifier,
    kind: z.enum(["PDF", "EINVOICE_XML", "EINVOICE_VALIDATION_REPORT"]),
    fileName: z.string().min(1).max(255),
    contentType: z.enum([
      "application/pdf",
      "application/xml",
      "text/xml",
      "application/json",
    ]),
    byteSize: z
      .number()
      .int()
      .positive()
      .max(25 * 1024 * 1024),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    downloadUrl: z
      .string()
      .regex(
        /^\/api\/documents\/[A-Za-z0-9_-]{1,128}\/delivery-artifacts\/[A-Za-z0-9_-]{1,128}$/,
      ),
  })
  .strict();
export const InvoiceDeliveryReviewSchema = z
  .object({
    tenantId: identifier,
    documentId: identifier,
    documentNumber: z.string().max(200),
    documentStatus: z.string().min(1).max(100),
    editRevision,
    reviewRevision,
    artifacts: z.array(InvoiceDeliveryArtifactSchema).max(3),
    pdf: z
      .object({
        state: z.enum(["REQUIRED", "READY", "FAILED"]),
        rendererVersion: z.string().min(1).max(100),
        reason: z
          .enum([
            "UNSUPPORTED_TEXT",
            "DOCUMENT_TOO_LARGE",
            "RENDER_FAILED",
            "ARTIFACT_CHANGED",
          ])
          .nullable(),
      })
      .strict(),
    canPrepare: z.boolean(),
    canDispatch: z.boolean(),
    delivery: InvoiceDeliveryStateSchema.nullable(),
    channels: z
      .object({
        EMAIL: z
          .object({
            available: z.boolean(),
            blockedReason: blocker.nullable(),
            from: z
              .object({
                email: z.string().email().max(254),
                name: z.string().max(200).nullable(),
                transport: OutboundTransportSchema,
              })
              .strict()
              .nullable(),
            recipientSuggestion: z.string().email().max(254).nullable(),
            subjectSuggestion: z.string().max(200),
            bodyTextSuggestion: z.string().max(20000),
          })
          .strict(),
        PEPPOL: z
          .object({
            available: z.boolean(),
            blockedReason: blocker.nullable(),
            receiverParticipantId: z
              .string()
              .regex(/^\d{4}:[^\s<>]{1,200}$/)
              .nullable(),
          })
          .strict(),
      })
      .strict(),
  })
  .strict()
  .superRefine((value, context) => {
    for (const [index, artifact] of value.artifacts.entries()) {
      const expected = `/api/documents/${value.documentId}/delivery-artifacts/${artifact.id}`;
      if (artifact.downloadUrl !== expected)
        context.addIssue({
          code: "custom",
          path: ["artifacts", index, "downloadUrl"],
          message: "Unconfirmed delivery artifact.",
        });
      const expectedType =
        artifact.kind === "PDF"
          ? ["application/pdf"]
          : artifact.kind === "EINVOICE_VALIDATION_REPORT"
            ? ["application/json"]
            : ["application/xml", "text/xml"];
      if (!expectedType.includes(artifact.contentType))
        context.addIssue({
          code: "custom",
          path: ["artifacts", index, "contentType"],
          message: "Unconfirmed artifact type.",
        });
    }
    const email = value.channels.EMAIL,
      peppol = value.channels.PEPPOL;
    const invalid =
      email.available === Boolean(email.blockedReason) ||
      peppol.available === Boolean(peppol.blockedReason) ||
      (email.available && (!email.from || value.pdf.state !== "READY")) ||
      (peppol.available && !peppol.receiverParticipantId) ||
      (value.pdf.state === "READY" &&
        !value.artifacts.some((a) => a.kind === "PDF")) ||
      (value.pdf.state !== "READY" &&
        value.artifacts.some((a) => a.kind === "PDF")) ||
      (value.pdf.state === "FAILED"
        ? !value.pdf.reason
        : value.pdf.reason !== null) ||
      (value.canDispatch &&
        (value.documentStatus !== "SENDING" ||
          value.delivery?.status !== "QUEUED")) ||
      (value.canPrepare &&
        (value.documentStatus !== "APPROVED" || value.pdf.state === "READY")) ||
      ((email.available || peppol.available) &&
        value.documentStatus !== "APPROVED") ||
      new Set(value.artifacts.map((a) => a.kind)).size !==
        value.artifacts.length;
    if (invalid)
      context.addIssue({
        code: "custom",
        message: "Unconfirmed delivery eligibility.",
      });
  });
export const InvoiceDeliveryDispatchSchema = z
  .object({ deliveryId: z.string().uuid() })
  .strict();
export type InvoiceDeliveryDispatch = z.infer<
  typeof InvoiceDeliveryDispatchSchema
>;
export const PrepareInvoiceDeliverySchema = z.object({ editRevision }).strict();
export const InvoiceDeliveryCommandSchema = z.discriminatedUnion("channel", [
  z
    .object({
      channel: z.literal("EMAIL"),
      editRevision,
      reviewRevision,
      recipient: z.string().email().max(254),
      subject: z
        .string()
        .trim()
        .min(1)
        .max(200)
        .regex(/^[^\r\n]*$/),
      bodyText: z.string().min(1).max(20000),
    })
    .strict(),
  z
    .object({
      channel: z.literal("PEPPOL"),
      editRevision,
      reviewRevision,
      receiverParticipantId: z.string().regex(/^\d{4}:[^\s<>]{1,200}$/),
    })
    .strict(),
]);
const receipt = z.object({
  tenantId: identifier,
  actorId: z.string().min(1).max(200),
  attemptKey: z.string().uuid(),
  recordedAt: z.string().datetime(),
});
export const InvoiceDeliveryAttemptSchema = z.discriminatedUnion("state", [
  receipt
    .extend({
      state: z.literal("ADMITTED"),
      documentId: identifier,
      channel: InvoiceDeliveryChannelSchema,
      editRevision,
      reviewRevision,
      deliveryId: identifier,
    })
    .strict(),
  receipt.extend({ state: z.literal("CANCELLED") }).strict(),
]);
export const InvoiceDeliveryAttemptResultSchema = z
  .object({
    attempt: InvoiceDeliveryAttemptSchema.nullable(),
    delivery: InvoiceDeliveryStateSchema.nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.delivery &&
      (value.attempt?.state !== "ADMITTED" ||
        value.attempt.deliveryId !== value.delivery.id ||
        value.attempt.channel !== value.delivery.channel)
    )
      context.addIssue({
        code: "custom",
        message: "Unconfirmed delivery result.",
      });
  });
export type InvoiceDeliveryReview = z.infer<typeof InvoiceDeliveryReviewSchema>;
export type PrepareInvoiceDelivery = z.infer<
  typeof PrepareInvoiceDeliverySchema
>;
export type InvoiceDeliveryCommand = z.infer<
  typeof InvoiceDeliveryCommandSchema
>;
export type InvoiceDeliveryAttempt = z.infer<
  typeof InvoiceDeliveryAttemptSchema
>;
export type InvoiceDeliveryState = z.infer<typeof InvoiceDeliveryStateSchema>;
export type InvoiceDeliveryAttemptResult = z.infer<
  typeof InvoiceDeliveryAttemptResultSchema
>;
export type InvoiceDeliveryArtifact = z.infer<
  typeof InvoiceDeliveryArtifactSchema
>;
export type InvoiceDeliveryBlockCode = z.infer<
  typeof InvoiceDeliveryBlockCodeSchema
>;
